import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CommentInput } from './CommentInput';
import type { Comment } from '@/hooks/useComments';
import ReactMarkdown from 'react-markdown';

import type { User } from '@/api/users';

interface CommentItemProps {
    comment: Comment;
    currentUserId?: string;
    onUpdate: (id: string, content: string) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    userMap?: Record<string, User>;
}

export function CommentItem({ comment, currentUserId, onUpdate, onDelete, userMap }: CommentItemProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const isOwner = comment.user_created?.id === currentUserId;
    const canEdit = isOwner; // Add admin check if needed

    const handleUpdate = async (content: string) => {
        await onUpdate(comment.id, content);
        setIsEditing(false);
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await onDelete(comment.id);
        } finally {
            setIsDeleting(false);
        }
    };

    const getUserName = (user: Comment['user_created']) => {
        if (!user) return 'Unknown User';
        if (user.first_name && user.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        return user.email || 'Unknown User';
    };

    const renderContent = (content: string) => {
        // Replace mentions @UUID with user names
        let contentWithMentions = content.replace(/@([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/g, (match, uuid) => {
            const user = userMap?.[uuid];
            if (user) {
                const name = user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.email;
                return `@${name}`;
            }
            return match;
        });

        // Also handle legacy @[UUID] just in case
        contentWithMentions = contentWithMentions.replace(/@\[([0-9a-fA-F-]+)\]/g, (match, uuid) => {
            const user = userMap?.[uuid];
            if (user) {
                const name = user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.email;
                return `@${name}`;
            }
            return match;
        });

        return (
            <div className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown>{contentWithMentions}</ReactMarkdown>
            </div>
        );
    };

    if (isEditing) {
        return (
            <div className="bg-background border rounded-lg p-4 mb-4">
                <CommentInput
                    value={comment.comment}
                    onSubmit={handleUpdate}
                    onCancel={() => setIsEditing(false)}
                    submitLabel="Save"
                    autoFocus
                />
            </div>
        );
    }

    return (
        <div className="group relative flex flex-col gap-2 p-4 rounded-lg hover:bg-accent/50 transition-colors border border-transparent hover:border-border">
            <div className="flex items-start justify-between gap-4">
                <div className="text-sm text-foreground wrap-break-word flex-1">
                    {renderContent(comment.comment)}
                </div>

                {canEdit && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity -mr-2 -mt-2">
                                <MoreVertical className="h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setIsEditing(true)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={handleDelete}
                                className="text-destructive focus:text-destructive"
                                disabled={isDeleting}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </div>

            <div className="flex items-center gap-2 mt-1">
                <Avatar className="h-5 w-5 border">
                    <AvatarImage src={comment.user_created?.avatar?.id ? `/assets/${comment.user_created.avatar.id}` : undefined} />
                    <AvatarFallback className="text-[9px]">
                        {comment.user_created?.first_name?.[0]}
                        {comment.user_created?.last_name?.[0]}
                    </AvatarFallback>
                </Avatar>

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground/80">
                        {getUserName(comment.user_created)}
                    </span>
                    <span>•</span>
                    <span>
                        {formatDistanceToNow(new Date(comment.date_created), { addSuffix: true })}
                        {comment.date_updated && comment.date_updated !== comment.date_created && ' (edited)'}
                    </span>
                </div>
            </div>
        </div>
    );
}
